'use client'

import { useMemo, useState } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Container from '@/components/structure/Container'
import StackCarousel from './StackCarousel'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import { cn, getBrandColors, hexToRgba, extractPrimaryCategory } from '@/lib/utils'
import type { App, Deal, Stack } from '@/payload-types'
import { easeShort } from '@/lib/transitions'

type StackHeroProps = {
  stack: Stack
  apps: App[]
  type?: 'root' | 'stack'
}

export default function StackHero({ stack, apps }: StackHeroProps) {
  const [selectedApp, setSelectedApp] = useState<App | null>(null)

  const selectedAppItem = useMemo(() => {
    const defaultColors = {
      brandBackgroundColor: '#FFF',
      branding: {
        primaryColor: '#ff34e7',
        secondaryColor: '#ff3465',
      },
    }

    if (!selectedApp) {
      return defaultColors
    }

    const { primaryColor, secondaryColor } = getBrandColors(selectedApp)

    if (!primaryColor || !secondaryColor) {
      return defaultColors
    }

    return {
      brandBackgroundColor: selectedApp.brandBackgroundColor,
      branding: {
        primaryColor,
        secondaryColor,
      },
    }
  }, [selectedApp])

  const backgroundGradient = `radial-gradient(50% 100% at 35% 0%, ${hexToRgba(selectedAppItem?.branding.primaryColor, 0.4)} 0%, rgba(0, 0, 0, 0.00) 80%), radial-gradient(50% 100% at 70% 0%, ${hexToRgba(selectedAppItem?.branding.secondaryColor, 0.4)} 0%, rgba(0, 0, 0, 0.00) 80%), #000`

  return (
    <Hero className="flex-col justify-center pt-32" height={{ expanded: true }}>
      {({ animations }) => (
        <Motion>
          <>
            <m.div
              className="absolute inset-0 -z-10"
              initial={false}
              animate={{
                background: backgroundGradient,
              }}
              transition={easeShort}
            />

            <Container className="relative z-10">
              <m.div style={animations} className="mx-auto w-fit">
                <StackHeroContent stack={stack} apps={apps} />
              </m.div>
            </Container>
            <StackCarousel apps={apps} selectedApp={selectedApp} setSelectedApp={setSelectedApp} />
          </>
        </Motion>
      )}
    </Hero>
  )
}

export function StackHeroContent({ stack, apps, type = 'stack' }: StackHeroProps) {
  const totalApps = apps.length
  const appDeals = apps.flatMap((app) => (app.deals?.docs || []) as Deal[])
  const totalDeals = appDeals.length
  const totalClaims = appDeals.reduce((total, deal) => total + (deal.claimed || 0), 0)

  const categories = useMemo(() => {
    const allUniqueCategories = Array.from(
      new Set(
        apps
          .map((app) => extractPrimaryCategory(app))
          .filter((name): name is string => name !== null && name !== undefined),
      ),
    )

    const hasMore = allUniqueCategories.length > 3
    const uniqueCategories = allUniqueCategories.slice(0, 3)

    if (uniqueCategories.length === 0) return ''
    if (uniqueCategories.length === 1) return uniqueCategories[0]
    if (uniqueCategories.length === 2) {
      return hasMore
        ? `${uniqueCategories[0]}, ${uniqueCategories[1]}, and more`
        : `${uniqueCategories[0]} and ${uniqueCategories[1]}`
    }
    if (uniqueCategories.length === 3) {
      return hasMore
        ? `${uniqueCategories[0]}, ${uniqueCategories[1]}, ${uniqueCategories[2]}, and more`
        : `${uniqueCategories[0]}, ${uniqueCategories[1]}, and ${uniqueCategories[2]}`
    }
    return ''
  }, [apps])

  const categorySentence = categories
    ? `Chosen for ${categories}.`
    : 'Chosen for teams across multiple categories.'

  if (!stack) return null
  return (
    <div className="flex h-fit max-w-screen-sm flex-col items-center justify-center gap-8 lg:items-start">
      <div
        className={cn(
          'flex max-w-screen-md flex-col items-center gap-4',
          type === 'root' ? 'lg:items-start' : '',
        )}
      >
        {type === 'root' && (
          <h1
            className={cn(
              'text-center font-sans text-12 font-medium uppercase tracking-widest text-white/40',
              'lg:text-left',
            )}
          >
            Business Software Stacks
          </h1>
        )}
        {type === 'root' ? (
          <span className="block text-balance text-center font-heading text-36 font-bold md:text-64 lg:text-left">
            {stack.name} Stack
          </span>
        ) : (
          <h1 className="block text-balance text-center font-heading text-36 font-bold md:text-64">
            {stack.name} Stack
          </h1>
        )}
        <p
          className={cn(
            'max-w-96 text-center text-14 leading-7 text-white/60 md:max-w-none md:text-balance md:text-16',
            type === 'root' ? 'lg:text-left' : '',
          )}
        >
          {totalApps} selected tools to power your business. {categorySentence}
        </p>
      </div>

      {type === 'root' && (
        <div className="flex flex-col items-center gap-6 lg:items-start">
          <Link href={`/stacks/${stack.slug}`} aria-label={`See ${stack.name} Stack`}>
            <Pill as="span" style="gradient" size="large">
              See {stack.name} Stack
            </Pill>
          </Link>
          {/* meticulous-ignore: totalClaims drifts between Meticulous runs as
              users actually claim deals (it's a live aggregate from
              deal.claimed). totalApps / totalDeals can also shift when admin
              adjusts a stack. Easier to ignore the whole pill than the
              numeric segments individually. */}
          <span data-review="ignore" className="meticulous-ignore text-12 text-white/60">
            {totalApps} app{totalApps === 1 ? '' : 's'}, {totalDeals} deal
            {totalDeals === 1 ? '' : 's'}, and {totalClaims} claim
            {totalClaims === 1 ? '' : 's'}
          </span>
        </div>
      )}
    </div>
  )
}
